import React from "react";

import { breadcrumbSchema, aboutPageSchema } from "../seo/structuredData";

import ContentPage from "./ContentPage";

/**
 * The page a prospect opens to answer "who is behind this and will they still be here in a year".
 *
 * Every claim here is one the rest of the site already makes (volunteer-built, per-event pricing, no
 * council affiliation), because an About page that quietly inflates the operation is the one thing
 * that makes an organizer distrust the other pages too.
 */
const sections = [
  {
    heading: "Why BadgeReg exists",
    body: [
      "A merit badge college is a genuinely hard event to run. A unit has to publish a class schedule, work out which Scouts can take which badge at which hour, keep every class under its capacity, chase down counselors, collect money from dozens of families, and then account for all of it afterwards. Most units do this with a spreadsheet, a paper form, and a shoebox of cheques.",
      "BadgeReg was built by volunteers who had run those events the hard way. It does the parts a computer should do (schedule conflicts, capacity limits, online payment, receipts, reporting), so the adults involved can spend their evenings on the programme instead of on reconciliation.",
    ],
  },
  {
    heading: "Who builds it",
    body: [
      "BadgeReg is built and maintained by Jacob Byerline with help from other Scouting volunteers. It is not a venture-backed company and it does not have a sales team. The person who writes the software is the person who answers the contact form.",
      "That has a practical upside and a practical limit. The upside is that feature requests from organizers get read by the developer and often shipped. The limit is that BadgeReg is a small operation, and we say so plainly rather than implying a support department that does not exist.",
    ],
  },
  {
    heading: "How it is funded",
    body: [
      "BadgeReg charges a one-time $250 setup fee per event plus $2.50 per registered Scout. There is no monthly subscription and no per-unit licence, because a unit that runs one event a year should not pay for twelve months of software.",
      "Those fees cover hosting, payment processing, and the time to support organizers through an event. They are not designed to build a business: most fundraisers run on BadgeReg raise upwards of $10,000, and the fee is a small fraction of that.",
    ],
  },
  {
    heading: "What we are not",
    body: [
      "We are deliberately clear about the boundaries of what we do, so nobody signs up expecting something else:",
    ],
    bullets: [
      "BadgeReg is not run by, endorsed by, or affiliated with a Scouting council or the Boy Scouts of America. It is independent software that Scouting units choose to use.",
      "It is not a general-purpose event ticketing platform. It is built around the merit badge college format specifically (Scouts, classes, counselors, and badges), which is why it fits that event better than a generic tool and would fit a conference worse.",
      "It is not a subscription. You pay per event.",
    ],
  },
  {
    heading: "Where it is going",
    body: [
      "Development is driven by the units actually running events. If something about your merit badge college does not fit how BadgeReg works, that is worth telling us: a good share of what the product does today started as an organizer's email. If you would like to fund a specific piece of work, the pricing page has a way to sponsor a feature.",
    ],
  },
];

export default function About() {
  return (
    <ContentPage
      route="/about"
      heading="About BadgeReg"
      badge="CitizenshipInTheCommunity.svg"
      badgeLabel="Citizenship in the Community"
      standfirst="BadgeReg is registration and fundraising software for Scouting merit badge colleges, built by volunteers who got tired of running those events on spreadsheets."
      sections={sections}
      jsonLd={[aboutPageSchema, breadcrumbSchema("About", "/about")]}
    />
  );
}
